/**
 * Helpers for service supply configuration.
 */

/**
 * @param {object|null} service
 * @returns {boolean}
 */
export function serviceHasSupplyConfig(service) {
  if (!service?.supplyConfig) return false;
  const qty = service.supplyConfig.quantity;
  const containers = service.supplyConfig.containers;
  return (Array.isArray(qty) && qty.length > 0) ||
    (Array.isArray(containers) && containers.length > 0);
}

/**
 * @param {Array<{ service: string }>} selectedServices
 * @param {Record<string, object>} servicesMap
 * @returns {boolean}
 */
export function appointmentNeedsSuppliesStep(selectedServices, servicesMap) {
  if (!Array.isArray(selectedServices)) return false;
  return selectedServices.some((sel) => {
    const svc = servicesMap[sel.service];
    return serviceHasSupplyConfig(svc);
  });
}

/**
 * @param {string[]} supplyIds
 * @param {Record<string, object>} suppliesMap
 * @returns {string[]}
 */
export function sortSupplyIdsByName(supplyIds, suppliesMap) {
  return [...supplyIds].sort((a, b) => {
    const nameA = (suppliesMap[a]?.name || a).toLocaleLowerCase('pt-BR');
    const nameB = (suppliesMap[b]?.name || b).toLocaleLowerCase('pt-BR');
    return nameA.localeCompare(nameB, 'pt-BR', { numeric: true, sensitivity: 'base' });
  });
}

/**
 * @param {object} group
 * @param {Record<string, object>} suppliesMap
 * @returns {string[]}
 */
export function resolveAllowedSupplyIds(group, suppliesMap) {
  if (!group) return [];
  if (Array.isArray(group.allowedSupplyIds) && group.allowedSupplyIds.length > 0) {
    return group.allowedSupplyIds;
  }
  if (!Array.isArray(group.allowedCategories) || group.allowedCategories.length === 0) {
    return [];
  }
  const cats = new Set(group.allowedCategories);
  return Object.values(suppliesMap || {})
    .filter((s) => s.category && cats.has(s.category))
    .map((s) => s.id);
}

/**
 * Build default quantity lines from service config.
 * @param {object} service
 * @returns {Array<{ kind: string, supplyId: string, quantity: number, serviceId: string }>}
 */
export function defaultQuantityLines(service) {
  const lines = [];
  const serviceId = service.id;
  const qtyList = service.supplyConfig?.quantity;
  if (!Array.isArray(qtyList)) return lines;
  for (const row of qtyList) {
    if (!row?.supplyId) continue;
    lines.push({
      kind: 'quantity',
      supplyId: row.supplyId,
      quantity: Number(row.defaultQuantity) || 1,
      serviceId,
    });
  }
  return lines;
}

/**
 * Merge last visit container lines with defaults for quantity.
 */
export function buildServiceSupplyState(service, lastVisitLines, suppliesMap) {
  const serviceId = service.id;
  const quantityLines = [];
  const containerSelected = {};
  const containerEvents = {};

  const qtyList = service.supplyConfig?.quantity || [];
  for (const row of qtyList) {
    if (!row?.supplyId) continue;
    const fromLast = lastVisitLines.find(
      (l) => l.kind === 'quantity' && l.supplyId === row.supplyId
    );
    quantityLines.push({
      kind: 'quantity',
      supplyId: row.supplyId,
      quantity: fromLast ? Number(fromLast.quantity) || 1 : Number(row.defaultQuantity) || 1,
      serviceId,
    });
  }

  const groups = service.supplyConfig?.containers || [];
  for (const group of groups) {
    const allowedIds = resolveAllowedSupplyIds(group, suppliesMap);
    for (const supplyId of allowedIds) {
      containerSelected[supplyId] = false;
      containerEvents[supplyId] = { opened: 0, depleted: 0 };
    }
  }

  for (const line of lastVisitLines) {
    if (line.kind === 'container' && line.supplyId) {
      containerSelected[line.supplyId] = true;
    }
  }

  return { quantityLines, containerSelected, containerEvents };
}

/**
 * Flatten wizard state to appointment fields.
 */
export function flattenSupplyPayload(serviceStates) {
  const supplyUsage = [];
  const containerEventsMap = {};

  for (const state of Object.values(serviceStates)) {
    for (const line of state.quantityLines || []) {
      if (Number(line.quantity) > 0) {
        supplyUsage.push({ ...line });
      }
    }
    for (const [supplyId, selected] of Object.entries(state.containerSelected || {})) {
      if (!selected) continue;
      supplyUsage.push({
        kind: 'container',
        supplyId,
        serviceId: state.serviceId,
      });
    }
    for (const [supplyId, ev] of Object.entries(state.containerEvents || {})) {
      const opened = Math.max(0, Math.floor(Number(ev.opened) || 0));
      const depleted = Math.max(0, Math.floor(Number(ev.depleted) || 0));
      if (opened === 0 && depleted === 0) continue;
      if (!containerEventsMap[supplyId]) {
        containerEventsMap[supplyId] = { supplyId, opened: 0, depleted: 0 };
      }
      containerEventsMap[supplyId].opened += opened;
      containerEventsMap[supplyId].depleted += depleted;
    }
  }

  return {
    supplyUsage,
    containerEvents: Object.values(containerEventsMap),
  };
}

/**
 * Container events relevant to one service's supply configuration.
 * @param {object} service
 * @param {Array} containerEvents
 * @param {Record<string, object>} suppliesMap
 * @returns {Record<string, { opened: number, depleted: number }>}
 */
export function containerEventsForService(service, containerEvents, suppliesMap) {
  const supplyIds = new Set();
  for (const group of service.supplyConfig?.containers || []) {
    for (const id of resolveAllowedSupplyIds(group, suppliesMap)) {
      supplyIds.add(id);
    }
  }
  const map = {};
  for (const ev of containerEvents || []) {
    if (!ev?.supplyId || !supplyIds.has(ev.supplyId)) continue;
    map[ev.supplyId] = {
      opened: Math.max(0, Math.floor(Number(ev.opened) || 0)),
      depleted: Math.max(0, Math.floor(Number(ev.depleted) || 0)),
    };
  }
  return map;
}

/**
 * Build wizard state from an existing appointment's supply payload.
 * @param {object} service
 * @param {Array} supplyUsage
 * @param {Array} containerEvents
 * @param {Record<string, object>} suppliesMap
 */
export function buildServiceSupplyStateFromAppointment(
  service,
  supplyUsage,
  containerEvents,
  suppliesMap
) {
  const serviceId = service.id;
  const serviceUsage = (supplyUsage || []).filter((line) => line.serviceId === serviceId);

  const quantityLines = [];
  const qtyList = service.supplyConfig?.quantity || [];
  for (const row of qtyList) {
    if (!row?.supplyId) continue;
    const fromAppt = serviceUsage.find(
      (line) => line.kind === 'quantity' && line.supplyId === row.supplyId
    );
    quantityLines.push({
      kind: 'quantity',
      supplyId: row.supplyId,
      quantity: fromAppt
        ? Math.max(0, Math.floor(Number(fromAppt.quantity) || 0))
        : Number(row.defaultQuantity) || 1,
      serviceId,
    });
  }

  const containerSelected = {};
  const containerEventsBySupply = {};
  const groups = service.supplyConfig?.containers || [];
  for (const group of groups) {
    for (const supplyId of resolveAllowedSupplyIds(group, suppliesMap)) {
      containerSelected[supplyId] = false;
      containerEventsBySupply[supplyId] = { opened: 0, depleted: 0 };
    }
  }

  for (const line of serviceUsage) {
    if (line.kind === 'container' && line.supplyId) {
      containerSelected[line.supplyId] = true;
    }
  }

  const evMap = containerEventsForService(service, containerEvents, suppliesMap);
  for (const [supplyId, ev] of Object.entries(evMap)) {
    if (!containerEventsBySupply[supplyId]) continue;
    containerEventsBySupply[supplyId] = ev;
    if (ev.opened > 0 || ev.depleted > 0) {
      containerSelected[supplyId] = true;
    }
  }

  return { quantityLines, containerSelected, containerEvents: containerEventsBySupply };
}

/**
 * Build wizard state from recorded usage when service supplyConfig is missing.
 * @param {string} serviceId
 * @param {Array} supplyUsage
 * @param {Array} containerEvents
 * @param {Record<string, object>} suppliesMap
 */
export function buildServiceSupplyStateFromUsageOnly(
  serviceId,
  supplyUsage,
  containerEvents,
  suppliesMap,
) {
  const serviceUsage = (supplyUsage || []).filter((line) => line.serviceId === serviceId);
  const quantityLines = serviceUsage
    .filter((line) => line.kind === 'quantity' && line.supplyId)
    .map((line) => ({
      kind: 'quantity',
      supplyId: line.supplyId,
      quantity: Math.max(0, Math.floor(Number(line.quantity) || 0)),
      serviceId,
    }));

  const containerSelected = {};
  const containerEventsBySupply = {};
  const containerSupplyIds = new Set();

  for (const line of serviceUsage) {
    if (line.kind !== 'container' || !line.supplyId) continue;
    containerSelected[line.supplyId] = true;
    containerSupplyIds.add(line.supplyId);
    containerEventsBySupply[line.supplyId] = { opened: 0, depleted: 0 };
  }

  for (const line of serviceUsage) {
    if (line.supplyId) containerSupplyIds.add(line.supplyId);
  }

  for (const ev of containerEvents || []) {
    if (!ev?.supplyId || !containerSupplyIds.has(ev.supplyId)) continue;
    containerEventsBySupply[ev.supplyId] = {
      opened: Math.max(0, Math.floor(Number(ev.opened) || 0)),
      depleted: Math.max(0, Math.floor(Number(ev.depleted) || 0)),
    };
    if (ev.opened > 0 || ev.depleted > 0) {
      containerSelected[ev.supplyId] = true;
    }
  }

  const containerGroups = containerSupplyIds.size > 0 ? [{
    groupId: 'recorded',
    groupLabel: 'Insumos registrados',
    allowedSupplyIds: sortSupplyIdsByName([...containerSupplyIds], suppliesMap),
  }] : [];

  return {
    quantityLines,
    containerSelected,
    containerEvents: containerEventsBySupply,
    containerGroups,
  };
}

/**
 * Aggregate per-supply container adjustments from appointment containerEvents.
 * @param {Array} containerEvents
 * @returns {Record<string, { opened: number, depleted: number }>}
 */
export function aggregateContainerAdjustments(containerEvents) {
  const map = {};
  for (const ev of containerEvents || []) {
    if (!ev?.supplyId) continue;
    const opened = Math.max(0, Math.floor(Number(ev.opened) || 0));
    const depleted = Math.max(0, Math.floor(Number(ev.depleted) || 0));
    if (opened === 0 && depleted === 0) continue;
    if (!map[ev.supplyId]) {
      map[ev.supplyId] = { opened: 0, depleted: 0 };
    }
    map[ev.supplyId].opened += opened;
    map[ev.supplyId].depleted += depleted;
  }
  return map;
}
